// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Share & Earn  (API + invite-link helpers for pages/ShareEarn.tsx and the sign-up step)
// GET /api/me/referrals → the customer's own code, the friends who signed up with it (first name + last initial only) and how far
// each got: joined → ordered → completed (their order was delivered). `rewardInr` is what a completed referral earns the referrer
// in wallet credit — 0 when ZenFresh hasn't switched a reward on, in which case the page only tracks referrals.
// The invite link is <site>/welcome?ref=CODE. Opening it remembers the code on this device (`captureReferralFromUrl`, called once
// at start-up) so the sign-up profile step can pre-fill it (`pendingReferral`) — the friend can still remove or change it.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";
import { REFERRAL_PATTERN } from "./profile";

export interface ReferredFriend {
  /** "Priya K." */
  name: string;
  /** "pending" = signed up with the code; "ordered" = placed a paid order; "completed" = that order was delivered. */
  status: "pending" | "ordered" | "completed";
  joinedAt: string;
  orderedAt: string | null;
  completedAt: string | null;
  rewardInr: number;
}

export interface ReferralSummary {
  code: string;
  rewardInr: number;
  invited: number;
  inProgress: number;
  completed: number;
  totalEarnedInr: number;
  referrals: ReferredFriend[];
}

export const fetchReferrals = () => apiAuthed<ReferralSummary>("GET", "/me/referrals");

export const referralLink = (code: string) => `${window.location.origin}/welcome?ref=${encodeURIComponent(code)}`;

/** The text a customer sends. No offer is promised to the friend: none exists beyond the welcome coupon every account gets. */
export const inviteMessage = (code: string) =>
  `Hey! Join me on ZenFresh for fresh coastal seafood delivered to your door. Use my code ${code} when you sign up: ${referralLink(code)}`;

const KEY = "zenfresh-pending-referral";

/** Remember a `?ref=CODE` from the address bar (only if it looks like a code). Storage can throw, so failure means "nothing". */
export function captureReferralFromUrl(): void {
  try {
    const code = new URLSearchParams(window.location.search).get("ref")?.trim();
    if (code && REFERRAL_PATTERN.test(code)) localStorage.setItem(KEY, code.toUpperCase());
  } catch {
    // Ignored on purpose: a lost invite code is not worth breaking the page.
  }
}

export function pendingReferral(): string {
  try {
    return localStorage.getItem(KEY) ?? "";
  } catch {
    return "";
  }
}
